import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import Notification from '../components/Notification';
import { useNotification } from '../hooks/useNotification';

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [courseData, setCourseData] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [moduleLessons, setModuleLessons] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const { notification, showError, showSuccess, hideNotification } = useNotification();
  const [sortConfig, setSortConfig] = useState({
    field: 'enrolledAt',
    direction: 'desc',
  });

  useEffect(() => {
    fetchCourseDetails();
  }, [id]);

  const fetchCourseDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/courses/${id}/details`);
      setCourseData(response.data.data);
      if (response.data.data.course) {
        setEditForm({
          title: response.data.data.course.title || '',
          description: response.data.data.course.description || '',
          individualPrice: response.data.data.course.individualPrice || 0,
          bulkPrice: response.data.data.course.bulkPrice || 0,
          estimatedHours: response.data.data.course.estimatedHours || null,
          isActive: response.data.data.course.isActive !== false,
        });
      }
    } catch (error) {
      console.error('Failed to fetch course details:', error);
      showError('Failed to load course details');
    } finally {
      setLoading(false);
    }
  };

  const fetchModuleLessons = async (moduleId) => {
    try {
      const response = await api.get(`/admin/modules/${moduleId}/lessons`);
      setModuleLessons(prev => ({
        ...prev,
        [moduleId]: response.data.data.lessons || [],
      }));
    } catch (error) {
      console.error('Failed to fetch module lessons:', error);
    }
  };

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId],
    }));
    if (!expandedModules[moduleId] && !moduleLessons[moduleId]) {
      fetchModuleLessons(moduleId);
    }
  };

  const handleSaveCourse = async () => {
    try {
      await api.patch(`/admin/courses/${id}`, editForm);
      await fetchCourseDetails();
      setEditMode(false);
      showSuccess('Course updated successfully');
    } catch (error) {
      console.error('Failed to update course:', error);
      showError(error.response?.data?.message || 'Failed to update course');
    }
  };

  const handleCreateLesson = (moduleId) => {
    navigate(`/modules/${moduleId}/lessons/new`);
  };

  const handleEditLesson = (lessonId) => {
    navigate(`/lessons/${lessonId}/edit`);
  };

  const handleEditExam = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (id) {
      navigate(`/courses/${id}/exam/edit`);
    } else {
      console.error('Course ID is missing');
    }
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return (
        <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortConfig.direction === 'asc' ? (
      <svg className="w-4 h-4 ml-1 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 ml-1 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const sortEnrollments = (enrollments) => {
    if (!enrollments || enrollments.length === 0) return enrollments;
    
    const sorted = [...enrollments];
    const { field, direction } = sortConfig;
    
    sorted.sort((a, b) => {
      let aValue, bValue;
      
      switch (field) {
        case 'user':
          aValue = `${a.user.firstName} ${a.user.lastName}`.toLowerCase();
          bValue = `${b.user.firstName} ${b.user.lastName}`.toLowerCase();
          break;
        case 'school':
          aValue = (a.user.school?.name || '').toLowerCase();
          bValue = (b.user.school?.name || '').toLowerCase();
          break;
        case 'enrolledAt':
          aValue = new Date(a.enrolledAt).getTime();
          bValue = new Date(b.enrolledAt).getTime();
          break;
        case 'completedAt':
          aValue = a.completedAt ? new Date(a.completedAt).getTime() : 0;
          bValue = b.completedAt ? new Date(b.completedAt).getTime() : 0;
          break;
        case 'status':
          aValue = a.completedAt ? 'completed' : 'in-progress';
          bValue = b.completedAt ? 'completed' : 'in-progress';
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading course details...</p>
        </div>
      </div>
    );
  }

  if (!courseData || !courseData.course) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Course not found</p>
        <button
          onClick={() => navigate('/courses')}
          className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primaryDark"
        >
          Back to Courses
        </button>
      </div>
    );
  }

  const { course, stats, enrollments, users } = courseData;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'stats', label: 'Statistics' },
    { id: 'enrollments', label: 'Enrollments' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/courses')}
            className="text-primary hover:text-primaryDark mb-2 flex items-center"
          >
            ← Back to Courses
          </button>
          <h1 className="text-3xl font-bold text-gray-800">{course.title}</h1>
          {course.description && (
            <p className="text-gray-600 mt-2">{course.description}</p>
          )}
        </div>
        <div className="flex items-center space-x-3">
          {course.isActive ? (
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
              Active
            </span>
          ) : (
            <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-semibold">
              Inactive
            </span>
          )}
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={handleEditExam}
              className="px-4 py-2 text-white rounded-lg transition-colors font-medium"
              style={{ backgroundColor: '#1A588B' }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#0b406c';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#1A588B';
              }}
            >
              Edit Exam
            </button>
            {activeTab === 'overview' && (
              <button
                type="button"
                onClick={() => setEditMode(!editMode)}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primaryDark transition-colors"
              >
                {editMode ? 'Cancel Edit' : 'Edit Course'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl shadow-md p-6">
        {activeTab === 'overview' && (
          <div>
            {editMode ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={editForm.description || ''}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Individual Price</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editForm.individualPrice}
                      onChange={(e) => setEditForm({ ...editForm, individualPrice: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Bulk Price</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editForm.bulkPrice}
                      onChange={(e) => setEditForm({ ...editForm, bulkPrice: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Hours</label>
                    <input
                      type="number"
                      value={editForm.estimatedHours || ''}
                      onChange={(e) => setEditForm({ ...editForm, estimatedHours: e.target.value ? parseInt(e.target.value) : null })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <label className="ml-2 text-sm text-gray-700">Active</label>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleSaveCourse}
                    className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primaryDark transition-colors"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={() => {
                      setEditMode(false);
                      fetchCourseDetails();
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Individual Price</p>
                    <p className="text-xl font-bold text-gray-800">${parseFloat(course.individualPrice).toFixed(2)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Bulk Price</p>
                    <p className="text-xl font-bold text-gray-800">${parseFloat(course.bulkPrice).toFixed(2)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Estimated Hours</p>
                    <p className="text-xl font-bold text-gray-800">{course.estimatedHours || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Modules</p>
                    <p className="text-xl font-bold text-gray-800">{course.modules?.length || 0}</p>
                  </div>
                </div>

                <h3 className="font-semibold text-gray-700 mb-4 text-lg">Modules</h3>
                <div className="space-y-2">
                  {course.modules?.map((module) => (
                    <div
                      key={module.id}
                      className="border border-gray-200 rounded-lg overflow-hidden"
                    >
                      <div className="p-3 bg-gray-50 flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => toggleModule(module.id)}
                            className="w-8 h-8 flex items-center justify-center bg-primary text-white rounded-lg hover:bg-primaryDark transition-colors shadow-sm hover:shadow-md font-bold text-lg"
                            title={expandedModules[module.id] ? 'Collapse' : 'Expand'}
                          >
                            {expandedModules[module.id] ? '▼' : '▶'}
                          </button>
                          <div>
                            <p className="font-medium text-gray-800">{module.title}</p>
                            {module.description && (
                              <p className="text-sm text-gray-600">{module.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-sm text-gray-500">
                            {moduleLessons[module.id]?.length || module.lessons?.length || 0} lessons
                          </span>
                          <button
                            onClick={() => handleCreateLesson(module.id)}
                            className="px-3 py-1 text-sm bg-primary text-white rounded hover:bg-primaryDark"
                          >
                            + New Lesson
                          </button>
                        </div>
                      </div>
                      {expandedModules[module.id] && (
                        <div className="p-3 bg-white border-t border-gray-200">
                          {moduleLessons[module.id] && moduleLessons[module.id].length > 0 ? (
                            <div className="space-y-2">
                              {moduleLessons[module.id].map((lesson) => (
                                <div
                                  key={lesson.id}
                                  className="flex justify-between items-center p-2 bg-gray-50 rounded hover:bg-gray-100"
                                >
                                  <div>
                                    <p className="font-medium text-sm text-gray-800">
                                      {lesson.title}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {lesson.lessonType} • Order: {lesson.order}
                                      {lesson.estimatedTime && ` • ${lesson.estimatedTime} min`}
                                    </p>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    {lesson.isActive ? (
                                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                                        Active
                                      </span>
                                    ) : (
                                      <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">
                                        Inactive
                                      </span>
                                    )}
                                    <button
                                      onClick={() => handleEditLesson(lesson.id)}
                                      className="px-4 py-1.5 text-sm font-medium text-primary border border-primary rounded hover:bg-primary hover:text-white transition-colors"
                                    >
                                      Edit Lesson
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-sm text-gray-500">
                              No lessons yet. Create a new lesson to get started.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'stats' && (
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-6">Course Statistics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <p className="text-sm text-gray-600 font-medium mb-2">Total Enrollments</p>
                <p className="text-4xl font-bold text-gray-900">{stats.totalEnrollments}</p>
              </div>
              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <p className="text-sm text-gray-600 font-medium mb-2">Completed</p>
                <p className="text-4xl font-bold text-gray-900">{stats.completedEnrollments}</p>
                {stats.totalEnrollments > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {Math.round((stats.completedEnrollments / stats.totalEnrollments) * 100)}% of total
                  </p>
                )}
              </div>
              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <p className="text-sm text-gray-600 font-medium mb-2">In Progress</p>
                <p className="text-4xl font-bold text-gray-900">{stats.inProgressEnrollments}</p>
                {stats.totalEnrollments > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {Math.round((stats.inProgressEnrollments / stats.totalEnrollments) * 100)}% of total
                  </p>
                )}
              </div>
              <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg p-6 border border-primary/20 shadow-sm">
                <p className="text-sm text-gray-700 font-medium mb-2">Completion Rate</p>
                <p className="text-4xl font-bold text-primary">{stats.completionRate}%</p>
                <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ width: `${stats.completionRate}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'enrollments' && (
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Enrollments ({enrollments.length})</h3>
            {enrollments.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No enrollments found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('user')}
                      >
                        <div className="flex items-center">
                          User
                          <SortIcon field="user" />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('school')}
                      >
                        <div className="flex items-center">
                          School
                          <SortIcon field="school" />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('enrolledAt')}
                      >
                        <div className="flex items-center">
                          Enrolled
                          <SortIcon field="enrolledAt" />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('completedAt')}
                      >
                        <div className="flex items-center">
                          Completed
                          <SortIcon field="completedAt" />
                        </div>
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center">
                          Status
                          <SortIcon field="status" />
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortEnrollments(enrollments).map((enrollment) => (
                      <tr key={enrollment.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {enrollment.user.firstName} {enrollment.user.lastName}
                            </p>
                            <p className="text-sm text-gray-500">{enrollment.user.email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {enrollment.user.school?.name || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(enrollment.enrolledAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {enrollment.completedAt ? new Date(enrollment.completedAt).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {enrollment.completedAt ? (
                            <div className="flex flex-col">
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold w-fit mb-1">
                                Completed
                              </span>
                              <span className="text-[10px] text-gray-400">
                                {enrollment.progress?.totalCount} / {enrollment.progress?.totalCount} Lessons
                              </span>
                            </div>
                          ) : (
                            <div className="flex flex-col min-w-[140px]">
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-xs font-semibold text-primary">
                                  {enrollment.progress?.percent}%
                                </span>
                                <span className="text-[10px] text-gray-500">
                                  {enrollment.progress?.completedCount} / {enrollment.progress?.totalCount}
                                </span>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-1.5 mb-1.5">
                                <div 
                                  className="bg-primary h-1.5 rounded-full transition-all duration-500"
                                  style={{ width: `${enrollment.progress?.percent}%` }}
                                ></div>
                              </div>
                              {enrollment.progress?.currentLesson && (
                                <span className="text-[10px] text-gray-500 truncate max-w-[150px]" title={enrollment.progress.currentLesson.title}>
                                  Current: {enrollment.progress.currentLesson.title}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => navigate(`/users/${enrollment.user.id}`)}
                            className="text-primary hover:text-primaryDark font-medium"
                          >
                            View User
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Notification */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
          duration={notification.duration}
        />
      )}
    </div>
  );
};

export default CourseDetail;

